import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Check, X as XIcon, Inbox, Mail, Building2, User, MessageSquare, Ticket, Plus, Trash2, UserPlus } from "lucide-react";

const RESEARCHER_LABEL = {
  independent:    "Independent",
  academic:       "Academic",
  pharmaceutical: "Pharmaceutical",
  clinical:       "Clinical Lab",
  veterinary:     "Veterinary",
  other:          "Other",
};

const STATUS_ORDER = ["new", "approved", "dismissed"];

/**
 * Admin > Access Requests tab.
 * Table of landing-page access submissions with status filter and per-row actions.
 */
export default function AdminAccessRequests() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("new");
  const [busyId, setBusyId] = useState(null);
  const [approvingId, setApprovingId] = useState(null);   // row currently in approval-note dialog
  const [personalNote, setPersonalNote] = useState("");
  const [mutations, setMutations] = useState(0);          // bump on each status change to refresh stats

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status_filter: filter };
      const { data } = await api.get("/admin/access-requests", { params });
      setRows(data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const counts = useMemo(() => {
    const c = { new: 0, approved: 0, dismissed: 0 };
    rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);

  const updateStatus = async (id, status, personal_note = "") => {
    setBusyId(id);
    try {
      await api.post(`/admin/access-requests/${id}/status`, { status, personal_note });
      toast.success(
        status === "approved"
          ? "Approved — magic-link emailed to applicant."
          : status === "dismissed"
            ? "Dismissed."
            : "Reopened."
      );
      setApprovingId(null);
      setPersonalNote("");
      setMutations((n) => n + 1);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="admin-access-requests">
      <AccessRequestsStatsStrip refreshKey={mutations} />
      <InviteCodesPanel />

      {/* Filter tabs */}
      <div className="mt-14 flex flex-wrap gap-2 mb-6 items-baseline">
        <h2 className="font-serif text-2xl text-charcoal mr-4">Requests</h2>
        {[
          { k: "new",       label: "New" },
          { k: "approved",  label: "Approved" },
          { k: "dismissed", label: "Dismissed" },
          { k: "all",       label: "All" },
        ].map(({ k, label }) => (
          <button
            key={k}
            type="button"
            onClick={() => setFilter(k)}
            className={`px-4 py-2 rounded-full text-[13px] uppercase tracking-[0.22em] border transition-colors ${
              filter === k
                ? "bg-charcoal text-bone border-charcoal"
                : "bg-white text-charcoal border-borderSubtle hover:border-charcoal"
            }`}
            data-testid={`access-filter-${k}`}
          >
            {label}
            {k !== "all" && counts[k] > 0 && (
              <span className={`ml-2 font-mono ${filter === k ? "text-bone/70" : "text-charcoal/75"}`}>
                {counts[k]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Empty state */}
      {loading ? (
        <div className="py-16 text-center text-charcoal/75 text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-borderSubtle rounded-2xl bg-white p-16 text-center">
          <div className="w-14 h-14 rounded-full bg-oxblood/10 grid place-items-center mx-auto">
            <Inbox size={22} strokeWidth={1.5} className="text-oxblood" />
          </div>
          <p className="mt-5 font-serif text-2xl text-charcoal">No {filter === "all" ? "" : filter} requests.</p>
          <p className="mt-2 text-sm text-charcoal/75">Submissions from the landing page will appear here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <RequestRow
              key={r.id}
              row={r}
              busy={busyId === r.id}
              onApprove={() => { setApprovingId(r.id); setPersonalNote(""); }}
              onDismiss={() => updateStatus(r.id, "dismissed")}
              onReopen={() => updateStatus(r.id, "new")}
            />
          ))}
        </div>
      )}

      {/* Approve-with-note dialog */}
      {approvingId && (() => {
        const row = rows.find((r) => r.id === approvingId);
        if (!row) return null;
        return (
          <div className="fixed inset-0 z-50 bg-charcoal/60 backdrop-blur-sm flex items-center justify-center p-6" data-testid="approve-note-dialog">
            <div className="bg-white rounded-2xl max-w-lg w-full p-8 border border-borderSubtle">
              <p className="text-[12px] uppercase tracking-[0.28em] text-oxblood font-mono">Approve access</p>
              <h3 className="mt-3 font-serif text-2xl text-charcoal tracking-tight">Send {row.name} a set-password link</h3>
              <p className="mt-3 text-sm text-charcoal/75">
                Approving creates their account and emails <span className="font-mono text-charcoal">{row.email}</span> a link to set their password. Add an optional personal note that'll appear in that email.
              </p>
              <textarea
                rows={4}
                value={personalNote}
                onChange={(e) => setPersonalNote(e.target.value)}
                placeholder="Optional — e.g. 'Glad to have you on board, Sarah. Reach out any time.'"
                className="w-full mt-5 p-3 border border-borderSubtle rounded-[4px] text-sm text-charcoal focus:outline-none focus:border-oxblood transition-colors resize-y min-h-[6rem]"
                data-testid="approve-note-input"
                maxLength={1000}
              />
              <div className="mt-6 pt-5 border-t border-borderSubtle flex flex-wrap gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => { setApprovingId(null); setPersonalNote(""); }}
                  className="px-4 py-2 rounded-full border border-borderSubtle text-charcoal text-[13px] uppercase tracking-[0.22em] hover:border-charcoal transition-colors"
                  data-testid="approve-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={busyId === approvingId}
                  onClick={() => updateStatus(approvingId, "approved", personalNote.trim())}
                  className="px-5 py-2 rounded-full bg-charcoal hover:bg-oxblood text-bone text-[13px] uppercase tracking-[0.22em] transition-colors disabled:opacity-40"
                  data-testid="approve-confirm-btn"
                >
                  {busyId === approvingId ? "Sending…" : "Approve & send link"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function RequestRow({ row, busy, onApprove, onDismiss, onReopen }) {
  const dt = row.created_at ? new Date(row.created_at) : null;
  const isNew = row.status === "new";
  return (
    <div
      className="border border-borderSubtle bg-white rounded-2xl p-5 sm:p-6"
      data-testid="access-request-row"
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <StatusPill status={row.status} />
            <span className="text-[12px] uppercase tracking-[0.24em] text-charcoal/75 font-mono">
              {RESEARCHER_LABEL[row.researcher_type] || row.researcher_type}
            </span>
            {dt && (
              <span className="text-[12px] text-charcoal/75 font-mono">
                {dt.toLocaleString()}
              </span>
            )}
          </div>
          <h3 className="mt-2 font-serif text-2xl text-charcoal tracking-tight">{row.name}</h3>

          <dl className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <Line icon={Mail}      value={<a href={`mailto:${row.email}`} className="text-charcoal hover:text-oxblood">{row.email}</a>} />
            <Line icon={Building2} value={row.institution} />
            {row.referred_by && (
              <Line icon={UserPlus} value={<><span className="text-charcoal/75 text-[12px] uppercase tracking-[0.22em] mr-2">Referred by</span>{row.referred_by}</>} />
            )}
          </dl>

          {row.message && (
            <div className="mt-4 pt-3 border-t border-borderSubtle flex gap-3">
              <MessageSquare size={14} strokeWidth={1.5} className="text-charcoal/75 shrink-0 mt-1" />
              <p className="text-sm text-charcoal leading-relaxed whitespace-pre-wrap">{row.message}</p>
            </div>
          )}

          {row.reviewed_at && (
            <p className="mt-3 text-[12px] uppercase tracking-[0.24em] text-charcoal/75 font-mono">
              Reviewed {new Date(row.reviewed_at).toLocaleString()}
              {row.reviewed_by && <> · by {row.reviewed_by}</>}
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-2 shrink-0">
          {isNew ? (
            <>
              <button
                type="button"
                onClick={onApprove}
                disabled={busy}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-charcoal text-bone text-[13px] uppercase tracking-[0.2em] hover:bg-oxblood transition-colors disabled:opacity-40"
                data-testid="access-approve-btn"
              >
                <Check size={14} strokeWidth={2} />
                Approve
              </button>
              <button
                type="button"
                onClick={onDismiss}
                disabled={busy}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-borderSubtle text-charcoal text-[13px] uppercase tracking-[0.2em] hover:border-oxblood hover:text-oxblood transition-colors disabled:opacity-40"
                data-testid="access-dismiss-btn"
              >
                <XIcon size={14} strokeWidth={2} />
                Dismiss
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onReopen}
              disabled={busy}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-borderSubtle text-charcoal text-[13px] uppercase tracking-[0.2em] hover:border-charcoal transition-colors disabled:opacity-40"
              data-testid="access-reopen-btn"
            >
              Reopen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const cfg = {
    new:       { text: "New",       cls: "bg-oxblood text-bone" },
    approved:  { text: "Approved",  cls: "bg-charcoal text-bone" },
    dismissed: { text: "Dismissed", cls: "bg-white text-charcoal/75 border border-borderSubtle" },
  }[status] || { text: status, cls: "bg-white text-charcoal/75" };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[12px] uppercase tracking-[0.22em] font-mono ${cfg.cls}`}>
      {cfg.text}
    </span>
  );
}

function Line({ icon: Icon, value }) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <Icon size={13} strokeWidth={1.5} className="text-charcoal/75 shrink-0" />
      <span className="text-charcoal truncate">{value}</span>
    </div>
  );
}

/**
 * Invite Codes CRUD panel — sits at the top of the Access Requests tab.
 * Trusted researchers hand these codes to family / friends for instant access.
 */
function InviteCodesPanel() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [maxUses, setMaxUses] = useState("1");
  const [unlimited, setUnlimited] = useState(false);
  const [expiresInDays, setExpiresInDays] = useState("0"); // 0 = never
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [editMaxUses, setEditMaxUses] = useState("1");
  const [editUnlimited, setEditUnlimited] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/invite-codes");
      setCodes(data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load invite codes");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const c = code.trim().toUpperCase();
    const mu = parseInt(maxUses, 10) || 1;
    const days = parseInt(expiresInDays, 10) || 0;
    if (!c) { toast.error("Enter a code"); return; }
    if (/\s/.test(c)) { toast.error("Codes can't contain spaces"); return; }
    setBusy(true);
    try {
      await api.post("/admin/invite-codes", {
        code: c,
        label: label.trim(),
        max_uses: mu,
        unlimited_uses: unlimited,
        expires_in_days: days,
      });
      toast.success(`Code ${c} ready to share.`);
      setCode(""); setLabel(""); setMaxUses("1"); setUnlimited(false); setExpiresInDays("0");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to create code");
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditLabel(c.label || "");
    setEditMaxUses(String(c.max_uses || 1));
    setEditUnlimited(!!c.unlimited_uses);
  };
  const cancelEdit = () => { setEditingId(null); };
  const saveEdit = async (c) => {
    try {
      await api.patch(`/admin/invite-codes/${c.id}`, {
        label: editLabel,
        max_uses: parseInt(editMaxUses, 10) || 1,
        unlimited_uses: editUnlimited,
      });
      toast.success("Code updated.");
      setEditingId(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update");
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/admin/invite-codes/${c.id}`, { active: !c.active });
      toast.success(c.active ? "Code deactivated." : "Code reactivated.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update");
    }
  };

  const deleteCode = async (c) => {
    if (!window.confirm(`Permanently DELETE code "${c.code}"?\n\nThis cannot be undone. Anyone still holding this code will get an error when they try to use it.`)) return;
    try {
      await api.delete(`/admin/invite-codes/${c.id}`);
      toast.success("Code deleted.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to delete");
    }
  };

  return (
    <div data-testid="admin-invite-codes">
      <div className="flex items-baseline gap-3 mb-4">
        <Ticket size={18} strokeWidth={1.5} className="text-oxblood" />
        <h2 className="font-serif text-2xl text-charcoal">Invite codes</h2>
        <span className="text-[12px] uppercase tracking-[0.24em] text-charcoal/75 font-mono">
          Fast-track access for trusted researchers
        </span>
      </div>
      <p className="text-sm text-charcoal/75 leading-relaxed max-w-2xl mb-6">
        Share a code with a trusted affiliate. When their contact fills the "Request Access" form with that code,
        the request is auto-approved and the applicant receives an instant set-password email.
      </p>

      {(() => {
        const unlabeledUsed = codes.filter((c) => !c.label && (c.uses || 0) > 0).length;
        const unlabeled = codes.filter((c) => !c.label).length;
        if (!unlabeled) return null;
        return (
          <div
            className={`mb-4 rounded-2xl border px-4 py-3 text-[13px] font-mono uppercase tracking-[0.18em] ${
              unlabeledUsed > 0
                ? "bg-oxblood/5 border-oxblood/30 text-oxblood"
                : "bg-bone border-borderSubtle text-charcoal/75"
            }`}
            data-testid="unlabeled-codes-callout"
          >
            {unlabeledUsed > 0 ? (
              <>
                <b>{unlabeledUsed}</b> used code{unlabeledUsed === 1 ? "" : "s"} still unlabeled ·{" "}
                <span className="normal-case tracking-normal font-sans">
                  Sign-up emails for these show no referrer. Click "Add label →" on the rows below.
                </span>
              </>
            ) : (
              <>
                <b>{unlabeled}</b> code{unlabeled === 1 ? "" : "s"} unlabeled ·{" "}
                <span className="normal-case tracking-normal font-sans">
                  Add a label so future sign-up emails tell you who referred them.
                </span>
              </>
            )}
          </div>
        );
      })()}

      <form onSubmit={create} className="border border-borderSubtle bg-white rounded-2xl p-5 mb-4 grid gap-3 sm:grid-cols-[1.2fr_2fr_.7fr_.9fr_auto] sm:items-end" data-testid="invite-code-form">
        <label className="block">
          <span className="overline">Code</span>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="E.g. SARA-2026"
            className="mt-1 w-full h-10 px-3 border border-borderSubtle rounded-[4px] text-sm font-mono tracking-[0.12em] uppercase focus:outline-none focus:border-oxblood"
            data-testid="invite-code-input"
            maxLength={60}
            required
          />
        </label>
        <label className="block">
          <span className="overline">Label (optional)</span>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Who's this for?"
            className="mt-1 w-full h-10 px-3 border border-borderSubtle rounded-[4px] text-sm focus:outline-none focus:border-oxblood"
            data-testid="invite-label-input"
            maxLength={200}
          />
        </label>
        <label className="block">
          <span className="overline">Max uses</span>
          <input
            type="number"
            min="1"
            max="10000"
            value={unlimited ? "" : maxUses}
            disabled={unlimited}
            placeholder={unlimited ? "Unlimited" : "1"}
            onChange={(e) => setMaxUses(e.target.value)}
            className="mt-1 w-full h-10 px-3 border border-borderSubtle rounded-[4px] text-sm focus:outline-none focus:border-oxblood disabled:bg-borderSubtle/20 disabled:text-charcoal/75"
            data-testid="invite-maxuses-input"
          />
          <span className="mt-1 flex items-center gap-1.5 text-[12px] uppercase tracking-[0.2em] text-charcoal/75 font-mono cursor-pointer">
            <input
              type="checkbox"
              checked={unlimited}
              onChange={(e) => setUnlimited(e.target.checked)}
              data-testid="invite-unlimited-toggle"
              className="w-3 h-3"
            />
            Unlimited uses
          </span>
        </label>
        <label className="block">
          <span className="overline">Expires</span>
          <select
            value={expiresInDays}
            onChange={(e) => setExpiresInDays(e.target.value)}
            className="mt-1 w-full h-10 px-3 border border-borderSubtle rounded-[4px] text-sm bg-white focus:outline-none focus:border-oxblood"
            data-testid="invite-expires-input"
          >
            <option value="0">Never</option>
            <option value="7">7 days</option>
            <option value="30">30 days</option>
            <option value="90">90 days</option>
            <option value="365">1 year</option>
          </select>
        </label>
        <button
          type="submit"
          disabled={busy}
          className="inline-flex items-center gap-2 px-5 h-10 rounded-full bg-charcoal text-bone text-[13px] uppercase tracking-[0.2em] hover:bg-oxblood transition-colors disabled:opacity-40"
          data-testid="invite-create-btn"
        >
          <Plus size={13} strokeWidth={2} />
          {busy ? "Creating…" : "Create"}
        </button>
      </form>

      {loading ? (
        <div className="text-sm text-charcoal/75">Loading…</div>
      ) : codes.length === 0 ? (
        <div className="text-sm text-charcoal/75">No codes yet. Create one above to fast-track a trusted contact.</div>
      ) : (
        <div className="border border-borderSubtle bg-white rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-bone text-charcoal/75">
              <tr>
                <th className="text-left px-4 py-2.5 overline font-normal">Code</th>
                <th className="text-left px-4 py-2.5 overline font-normal">Label</th>
                <th className="text-left px-4 py-2.5 overline font-normal">Uses</th>
                <th className="text-left px-4 py-2.5 overline font-normal">Expires</th>
                <th className="text-left px-4 py-2.5 overline font-normal">Status</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => {
                const exp = c.expires_at ? new Date(c.expires_at) : null;
                const expired = exp && exp < new Date();
                return (
                  <tr key={c.id} className="border-t border-borderSubtle" data-testid="invite-code-row">
                    <td className="px-4 py-3 font-mono tracking-[0.12em] text-charcoal">{c.code}</td>
                    <td className="px-4 py-3 text-charcoal/75">
                      {editingId === c.id ? (
                        <input
                          type="text"
                          value={editLabel}
                          onChange={(e) => setEditLabel(e.target.value)}
                          placeholder="e.g. Reddit AMA, Dr. Smith Clinic"
                          autoFocus
                          className="w-full h-8 px-2 border border-borderSubtle rounded-[4px] text-sm focus:outline-none focus:border-oxblood"
                          data-testid={`invite-edit-label-${c.code}`}
                        />
                      ) : c.label ? (
                        <span className="text-charcoal" data-testid={`invite-label-value-${c.code}`}>{c.label}</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => startEdit(c)}
                          className={`inline-flex items-center gap-1.5 text-[13px] uppercase tracking-[0.18em] font-mono transition-colors ${
                            (c.uses || 0) > 0
                              ? "text-oxblood hover:text-charcoal"
                              : "text-charcoal/75/70 hover:text-oxblood"
                          }`}
                          data-testid={`invite-add-label-${c.code}`}
                          title={(c.uses || 0) > 0
                            ? "This code has been used but has no label — you won't know who redeemed it in future admin emails. Click to label."
                            : "Add a label like 'Reddit AMA' or 'Dr. Smith Clinic' — it'll appear in the sign-up email so you know who referred them."}
                        >
                          {(c.uses || 0) > 0 && <span className="text-oxblood">●</span>}
                          Add label →
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-charcoal">
                      {editingId === c.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="1"
                            value={editUnlimited ? "" : editMaxUses}
                            disabled={editUnlimited}
                            onChange={(e) => setEditMaxUses(e.target.value)}
                            className="w-16 h-8 px-2 border border-borderSubtle rounded-[4px] text-sm focus:outline-none focus:border-oxblood disabled:bg-borderSubtle/20"
                            data-testid={`invite-edit-max-${c.code}`}
                          />
                          <label className="inline-flex items-center gap-1 text-[12px] uppercase tracking-[0.2em] text-charcoal/75 font-mono">
                            <input
                              type="checkbox"
                              checked={editUnlimited}
                              onChange={(e) => setEditUnlimited(e.target.checked)}
                              data-testid={`invite-edit-unlimited-${c.code}`}
                              className="w-3 h-3"
                            />
                            ∞
                          </label>
                        </div>
                      ) : (
                        c.unlimited_uses
                          ? <span data-testid={`invite-uses-${c.code}`}>{c.uses} <span className="text-charcoal/75 text-[13px]">/ ∞</span></span>
                          : <span data-testid={`invite-uses-${c.code}`}>{c.uses} / {c.max_uses}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-charcoal">
                      {exp ? (
                        <span className={expired ? "text-oxblood" : "text-charcoal"}>
                          {exp.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                          {expired && <span className="ml-2 text-[12px] uppercase tracking-[0.2em]">expired</span>}
                        </span>
                      ) : (
                        <span className="text-charcoal/75">Never</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {c.active ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-charcoal text-bone text-[12px] uppercase tracking-[0.22em] font-mono">Active</span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-white border border-borderSubtle text-charcoal/75 text-[12px] uppercase tracking-[0.22em] font-mono">Off</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {editingId === c.id ? (
                        <div className="inline-flex items-center gap-3">
                          <button type="button" onClick={() => saveEdit(c)} className="text-[12px] uppercase tracking-[0.22em] text-charcoal hover:text-oxblood transition-colors" data-testid={`invite-save-${c.code}`}>Save</button>
                          <button type="button" onClick={cancelEdit} className="text-[12px] uppercase tracking-[0.22em] text-charcoal/75 hover:text-charcoal transition-colors" data-testid={`invite-cancel-${c.code}`}>Cancel</button>
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-3">
                          <button type="button" onClick={() => startEdit(c)} className="text-[12px] uppercase tracking-[0.22em] text-charcoal/75 hover:text-charcoal transition-colors" data-testid={`invite-edit-${c.code}`}>Edit</button>
                          <button type="button" onClick={() => toggleActive(c)} className="text-[12px] uppercase tracking-[0.22em] text-charcoal/75 hover:text-charcoal transition-colors" data-testid={`invite-toggle-active-${c.code}`}>{c.active ? "Deactivate" : "Reactivate"}</button>
                          <button type="button" onClick={() => deleteCode(c)} className="inline-flex items-center gap-1 text-[12px] uppercase tracking-[0.22em] text-charcoal/75 hover:text-oxblood transition-colors" data-testid={`invite-delete-${c.code}`}>
                            <Trash2 size={12} strokeWidth={1.5} /> Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─── Analytics strip: N new · N approved · avg approval time ─────────── */
function AccessRequestsStatsStrip({ refreshKey }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/admin/access-requests/stats");
        if (alive) setStats(data);
      } catch {
        /* silent — analytics is decorative, not blocking */
      }
    })();
    return () => { alive = false; };
  }, [refreshKey]);

  if (!stats) return null;

  const avg = stats.avg_approval_hours;
  const avgLabel =
    avg == null
      ? "—"
      : avg < 1
        ? `${Math.max(1, Math.round(avg * 60))} min`
        : avg < 24
          ? `${avg} hr`
          : `${(avg / 24).toFixed(1)} d`;

  const cells = [
    { label: "New",             value: stats.new_count,            testid: "access-stats-new" },
    { label: "Approved",        value: stats.approved_count,       testid: "access-stats-approved" },
    { label: "Avg approval",    value: avgLabel,                   testid: "access-stats-avg" },
  ];

  return (
    <div
      className="mb-8 grid grid-cols-3 rounded-[6px] border border-borderSubtle bg-white overflow-hidden"
      data-testid="access-stats-strip"
    >
      {cells.map((c, i) => (
        <div
          key={c.label}
          className={`px-5 sm:px-7 py-4 flex flex-col ${i < cells.length - 1 ? "border-r border-borderSubtle" : ""}`}
        >
          <span className="text-[12px] uppercase tracking-[0.24em] text-charcoal/75 font-mono">{c.label}</span>
          <span className="mt-2 font-serif text-2xl sm:text-3xl text-charcoal leading-none" data-testid={c.testid}>
            {c.value}
          </span>
        </div>
      ))}
    </div>
  );
}

